<template>
  <fin-data-loader :resolver="loadData">
    <template #default>
      <div class="fin-user-account">

        <div class="pb-10">
          <div class="headline pb-2">
            <v-icon class="fin-color-primary-dark--text">
              icon-account
            </v-icon>
            Mein Konto
          </div>
          <p> Hier findest du alle Einstellungen rund um dein finCraft Konto.</p>
        </div>

        <fin-my-profile-block title="Zugangsdaten">
          <fin-editable-property v-model="password"
                                 :edit-function="changePassword"
                                 :orginal-value="{}"
                                 editableTextFieldName
                                 name="Passwort"
                                 submit-button="Ändern"
                                 title="Passwort ändern"
                                 v-if="!isExternalProviderLogin">

            <template #value>
              ******
            </template>


            <template #default="{model,change}">
              <fin-password-input v-model="model.oldPassword"
                                  label="altes Passwort"
                                  update
                                  @input="() => change(model)"/>
              <fin-password-input v-model="model.newPassword"
                                  :password-policy="passwordPolicy"
                                  class="mb-7"
                                  label="neues Passwort"
                                  password-check-enabled
                                  show-password-policy
                                  update
                                  @input="() => change(model)"/>
              <fin-password-input v-model="model.confirmNewPassword"
                                  :password-policy="[...passwordPolicy,confirmPasswordPolicy(model.newPassword)]"
                                  class="mb-7"
                                  label="Passwort erneut eingeben"
                                  password-check-enabled
                                  show-password-policy
                                  update
                                  @input="() => change(model)"
              />
            </template>
          </fin-editable-property>

          <div v-if="isIosOrAndroidApp && biometricAvailableOnDevice && !isExternalProviderLogin">
            <div class="value-name pb-0 mb-0">Biometrische Authentifizierung</div>
            <v-switch v-model="biometricAuthSwitchStatus"
                      :label="`${biometricAuthSwitchStatus ? 'wird verwendet': 'ist abgeschaltet'}`"
                      class="mt-0"
                      hide-details
            />
          </div>

          <fin-editable-property v-model="user.attributes.email"
                                 :edit-function="changeUserMail"
                                 :orginal-value="user.attributes.email"
                                 editableTextFieldName
                                 name="E-Mail"
                                 submit-button="Ändern"
                                 title="E-Mail ändern"
                                 :externalProviderLogin="isExternalProviderLogin">
            <template #default="{model,change}">
              <p v-if="hasNewsletterRegistration">Hinweis:<br/>Wenn du deine E-Mail-Adresse änderst, musst du dich
                erneut für den Newsletter registrieren.</p>
              <v-text-field
                :value="user.attributes.email"
                disabled
                label="Bisherige E-Mail"
              />
              <v-text-field
                :rules="[
                  v => !!v || 'E-Mail wird benötigt',
                  v => /.+@.+\..+/.test(v) || 'E-Mail muss gültig sein',
                  ]"
                :value="model"
                label="Neue E-Mail"
                @input="(val) => change(val)"
              />
              <v-text-field
                :rules="[
                  v => !!v || 'E-Mail wird benötigt',
                  v => /.+@.+\..+/.test(v) || 'E-Mail muss gültig sein',
                  v => v === model || 'Die E-Mail Adressen müssen identisch sein'
                  ]"
                class="mt-3 mb-3"
                label="E-Mail erneut eingeben"
              />
            </template>
          </fin-editable-property>

          <div v-if="!user.attributes.email_verified"
               class="mb-4">
            Bitte bestätige deine geänderte E-Mail Adresse durch
            <v-btn :to="{ name: 'finVerifyEmail'}" text>Eingabe des Verifikationscodes</v-btn>
            .
          </div>

          <fin-editable-property v-model="profile.username"
                                 :editFunction="updateUsername"
                                 :orginal-value="profile.username"
                                 editableTextFieldName
                                 name="Nutzername"
                                 submit-button="Ändern"
                                 title="Nutzername ändern">
            <template #default="{model,change}">
              <v-text-field
                :rules="[ v => !!v || 'Username wird benötigt']"
                :value="model"
                label="Nutzername"
                @input="change"
              />
            </template>
          </fin-editable-property>
        </fin-my-profile-block>

        <fin-subscribe-to-newsletter @input="val => hasNewsletterRegistration = val"/>

        <fin-gdpr-requests :paymentStatus="profile.paymentStatus"/>
      </div>
    </template>
  </fin-data-loader>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import FinEditableProperty from '@/components/pages/profile/EditableProperty'
import FinPasswordInput from '@/components/common/form/PasswordInput'
import FinSubscribeToNewsletter from '@/components/pages/profile/NewsletterSubscribe'
import FinPwdPolicy from "@/js/finPwdPolicy"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import {updateUserEmail, updateUserNewsletterSubscription} from "@/js/finMutations"
import FinDataLoader from "@/components/common/DataLoader"
import FinMyProfileBlock from "@/components/pages/profile/MyProfileBlock"
import FinGdprRequests from "@/components/pages/profile/GdprRequests"
import finBrowser from "@/js/finBrowser"
import {NativeBiometric} from "capacitor-native-biometric"

export default {
  name: 'fin-user-account',
  components: {
    FinGdprRequests,
    FinMyProfileBlock,
    FinDataLoader,
    FinSubscribeToNewsletter,
    FinPasswordInput,
    FinEditableProperty
  },
  data() {
    return {
      isIosOrAndroidApp: finBrowser.IS_IOS_OR_ANDROID_APP,
      changePasswordDialog: false,
      changeUserNameDialog: false,
      password: {},
      passwordPolicy: FinPwdPolicy.policy,
      hasNewsletterRegistration: false,
      biometricAvailableOnDevice: false,
      biometricAuthSwitchStatus: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['profile', 'user', 'userId', 'userEmail', 'userNewsletterSubscribed']),
    isExternalProviderLogin() {
      return this.user.username.startsWith("SignInWithApple_") || this.user.username.startsWith("Google_")
    }
  },
  methods: {
    ...mapActions('auth', ['updateUsername']),
    confirmPasswordPolicy(newPassword) {
      return {
        descDE: 'Passwörter müssen übereinstimmen',
        showRule: true,
        check(pwd) {
          return pwd && pwd === newPassword
        }
      }
    },
    async loadData() {
      this.hasNewsletterRegistration = this.userNewsletterSubscribed
    },
    changePassword(form) {
      const {oldPassword, newPassword} = form

      return new Promise((resolve, reject) => {
        this.$Amplify.Auth.currentAuthenticatedUser().then(user => {
          this.$Amplify.Auth.changePassword(user, oldPassword, newPassword)
            .then(data => {
              console.log('TODO track', data)
              resolve('success')
              this.$toasted.global.finToast({
                message: 'Dein Passwort wurde erfolgreich geändert.',
                type: 'success'
              })
            })
            .catch(reject)
        }).catch(reject)
      })
    },
    async unsubscribeNewsletter() {
      try {
        let newsletterSubscriptionResult = await API.graphql(graphqlOperation(updateUserNewsletterSubscription,
          {input: {id: this.userId, newsletter: false}}))
        console.log("newsletterSubscriptionResult", newsletterSubscriptionResult)
      } catch (error) {
        console.error("failed to unsubscribe the user to the newsletter while changing the user's email address", this.userEmail, error)
        this.$toasted.global.finToast({
          message: 'Bei der Newsletter-Abmeldung trat ein Fehler auf. Lade die Seite erneut und pass deine Registrierung zur Newsletter gegebenenfalls an.',
          type: 'error'
        })
      }
    },
    async changeUserMail(newMail) {
      newMail = newMail.trim()

      // After a user changed his email address, the user is no more subscribed to the newsletter. He has to subscribe to the newsletter with his new email address.
      if (this.hasNewsletterRegistration) {
        await this.unsubscribeNewsletter()
      }

      const user = await this.$Amplify.Auth.currentAuthenticatedUser()

      try {
        // the cognito attribute email and user table email field are not synchronized automatically
        // => update user email in cognito attribute and in user table explicitly

        await this.$Amplify.Auth.updateUserAttributes(user, {email: newMail}, {
          preferredLang: 'de_DE',
          signUpHost: window.location.origin
        })

        await this.$store.dispatch('auth/fetchUser')

        await API.graphql(graphqlOperation(updateUserEmail,
          {input: {id: this.userId, email: newMail}}))

        await this.$router.push({name: 'finVerifyEmail'})
        this.$toasted.global.finToast({
          message: 'Deine E-Mail wurde erfolgreich geändert. Bitte bestätige diese mit dem per Mail gesendeten Verifikationscode.',
          type: 'success'
        })
      } catch (error) {
        console.error("failed to change user's email", user, newMail, error)
        this.$toasted.global.finToast({
          message: 'Es ist ein technischer Fehler aufgetreten. Deine E-Mail konnte nicht geändert werden. Versuch es bitte später nochmals.',
          type: 'error'
        })
      }
    },
  },
  async mounted() {
    try {
      const result = await NativeBiometric.isAvailable()
      this.biometricAvailableOnDevice = result.isAvailable
      if (this.biometricAvailableOnDevice) {
        this.biometricAuthSwitchStatus = (localStorage.getItem(finBrowser.BIOMETRIC_AUTH_KEY) === 'true') || false
      }
    } catch (e) {
      //
    }
  },
  watch: {
    biometricAuthSwitchStatus() {
      localStorage.setItem(finBrowser.BIOMETRIC_AUTH_KEY, '' + this.biometricAuthSwitchStatus)
    }
  },
}
</script>

<style scoped>
.statusColor {
  line-height: 30px;
  font-size: 12px;
}

.status {
  font-size: 20px;
  font-weight: bold;
}

.value-name {
  font-size: 20px;
  font-weight: bold;
}
</style>
